import { ApolloLink, FetchResult, NextLink, Operation } from "@apollo/client"
import { Observable } from "@apollo/client/utilities"
import { shouldCollectMemoryTelemetry } from "./memoryMonitor"

/**
 * Track active subscriptions for memory monitoring
 * Only tracked when memory monitoring is enabled
 */
let activeSubscriptionCount = 0

/**
 * Get the current count of active subscriptions
 */
export function getActiveSubscriptionCount(): number {
  return activeSubscriptionCount
}

/**
 * Reset the subscription counter (for testing purposes)
 */
export function resetSubscriptionCount(): void {
  activeSubscriptionCount = 0
}

/**
 * Apollo Link for Azure Application Insights telemetry
 * Only tracks operations on the server-side (Node.js environment)
 */
export class AzureInsightsLink extends ApolloLink {
  constructor(private options?: { userId?: string; sessionId?: string }) {
    super()
  }

  request(operation: Operation, forward: NextLink): Observable<FetchResult> {
    return new Observable((observer) => {
      const startTime = Date.now()
      let subscription: any = null
      let subscriptionCounted = false

      // Track subscription creation if monitoring is enabled
      if (
        shouldCollectMemoryTelemetry() &&
        typeof window === "undefined" &&
        process.env.AZURE_APPLICATION_INSIGHTS_CONNECTION_STRING
      ) {
        activeSubscriptionCount++
        subscriptionCounted = true
        console.log(
          `[Azure Insights] Subscription created for ${operation.operationName}. Active count: ${activeSubscriptionCount}`,
        )
      }

      // Only track if we're in a Node.js environment and telemetry is available
      if (
        typeof window === "undefined" &&
        process.env.AZURE_APPLICATION_INSIGHTS_CONNECTION_STRING
      ) {
        // Import telemetry functions dynamically for server-side only
        import("../../apps/egged/lib/telemetry")
          .then(({ trackGraphQLOperation, trackError }) => {
            const graphqlOperation = {
              operationName: operation.operationName || "UnknownOperation",
              query: operation.query.loc?.source.body || "",
              variables: operation.variables,
            }

            trackGraphQLOperation(
              graphqlOperation,
              async () => {
                return new Promise<FetchResult>((resolve, reject) => {
                  subscription = forward(operation).subscribe({
                    next: (result) => {
                      const duration = Date.now() - startTime

                      // Track GraphQL errors if present
                      if (result.errors && result.errors.length > 0) {
                        result.errors.forEach((error) => {
                          trackError(
                            new Error(`GraphQL Error: ${error.message}`),
                            `Operation: ${operation.operationName}`,
                            {
                              userId: this.options?.userId,
                              sessionId: this.options?.sessionId,
                              customProperties: {
                                "graphql.operation.name":
                                  operation.operationName,
                                "graphql.error.path": error.path?.join("."),
                                "graphql.operation.duration": duration,
                              },
                            },
                          )
                        })
                      }

                      observer.next(result)
                      resolve(result)
                    },
                    error: (error) => {
                      const duration = Date.now() - startTime

                      trackError(
                        error,
                        `GraphQL Network Error: ${operation.operationName}`,
                        {
                          userId: this.options?.userId,
                          sessionId: this.options?.sessionId,
                          customProperties: {
                            "graphql.operation.name": operation.operationName,
                            "graphql.operation.duration": duration,
                            "error.type": "network_error",
                          },
                        },
                      )

                      observer.error(error)
                      reject(error)
                    },
                    complete: () => {
                      observer.complete()
                    },
                  })
                })
              },
              {
                userId: this.options?.userId,
                sessionId: this.options?.sessionId,
                customProperties: {
                  "graphql.operation.variables": JSON.stringify(
                    operation.variables,
                  ),
                },
              },
            ).catch((error) => {
              observer.error(error)
            })
          })
          .catch(() => {
            // Fallback if telemetry import fails - just forward the operation
            subscription = forward(operation).subscribe(observer)
          })
      } else {
        // Client-side or no telemetry - just forward the operation
        subscription = forward(operation).subscribe(observer)
      }

      // CRITICAL: Return teardown function to properly unsubscribe
      return () => {
        if (subscription) {
          subscription.unsubscribe()
        }

        // Track subscription cleanup if monitoring is enabled
        if (subscriptionCounted) {
          activeSubscriptionCount--
          if (shouldCollectMemoryTelemetry()) {
            console.log(
              `[Azure Insights] Subscription cleaned up for ${operation.operationName}. Active count: ${activeSubscriptionCount}`,
            )
          }
        }
      }
    })
  }

  /**
   * Update user context for telemetry
   */
  updateContext(userId?: string, sessionId?: string) {
    this.options = { userId, sessionId }
  }
}

/**
 * Create the Azure Insights Apollo Link
 * Uses singleton pattern on server-side to prevent memory leaks
 */
let azureInsightsLinkInstance: AzureInsightsLink | null = null

export function createAzureInsightsLink(options?: {
  userId?: string
  sessionId?: string
}) {
  // On server-side, reuse singleton instance
  if (typeof window === "undefined") {
    if (!azureInsightsLinkInstance) {
      azureInsightsLinkInstance = new AzureInsightsLink(options)
    }
    // Update context if options provided
    if (options) {
      azureInsightsLinkInstance.updateContext(options.userId, options.sessionId)
    }
    return azureInsightsLinkInstance
  }

  // On client-side, create new instance each time
  return new AzureInsightsLink(options)
}
